import { IconRemove, Switch } from '@newfront-insurance/core-ui';
import { ArrowLeft, Pencil, Share2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';

import { getCustomSkillDisplayText, getUpdatedLabel, hasUnpublishedChanges } from './custom-skill-model';
import { InlineIconButton } from './inline-icon-button';
import { CustomSkillSharesSummary } from './sharing/custom-skill-shares-summary';
import type { CustomSkillRecord } from '../../../../queries/chats';

function CustomSkillDetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="font-['Open_Sans',sans-serif] text-[12px] leading-4 font-semibold text-[#546A83]">{label}</dt>
      <dd className="mt-1 min-w-0 font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#262D46]">{children}</dd>
    </div>
  );
}

function CustomSkillPromptPreview({ skill }: { skill: CustomSkillRecord }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const promptPreview = getCustomSkillDisplayText(skill.frontmatter.overview, skill.frontmatter.description);
  const shouldClampPrompt = promptPreview.length > 360;

  return (
    <div>
      <div
        className={[
          "rounded-[4px] border border-[#DEE2E7] bg-[#FAFBFC] p-4 font-['Open_Sans',sans-serif] whitespace-pre-wrap",
          'text-[13px] leading-5 text-[#262D46]',
          shouldClampPrompt && !isExpanded ? 'max-h-[188px] overflow-hidden' : '',
        ].join(' ')}
      >
        {promptPreview}
      </div>
      {shouldClampPrompt ? (
        <button
          type="button"
          className="mt-3 font-['Open_Sans',sans-serif] text-[14px] leading-5 font-semibold text-[#0957C3]"
          onClick={() => setIsExpanded((currentValue) => !currentValue)}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}

export function CustomSkillDetailView({
  skill,
  isTogglePending,
  isPublishing,
  showSharingControls,
  onBack,
  onEdit,
  onToggle,
  onSharingChanged,
  onDelete,
  onPublishDraft,
}: {
  skill: CustomSkillRecord;
  isTogglePending: boolean;
  isPublishing: boolean;
  showSharingControls: boolean;
  onBack: () => void;
  onEdit: () => void;
  onToggle: () => void;
  onSharingChanged: () => Promise<void>;
  onDelete: () => void;
  onPublishDraft: () => void;
}) {
  const [isAddingPeople, setIsAddingPeople] = useState(false);
  const hasDraft = hasUnpublishedChanges(skill);
  const description = getCustomSkillDisplayText(skill.frontmatter.summary, skill.frontmatter.description);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-5 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            className="inline-flex min-w-0 items-center gap-2 font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#546A83] hover:text-[#262D46]"
            onClick={onBack}
            aria-label="Back to skills"
          >
            <ArrowLeft aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span className="truncate">
              <span>Skills</span>
              <span className="px-1 text-[#98A6B5]">/</span>
              <span className="font-semibold text-[#262D46]">/{skill.command}</span>
            </span>
          </button>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="mr-1 flex h-6 items-center gap-2 border-r border-[#DEE2E7] pr-3">
            <span className="font-['Open_Sans',sans-serif] text-[12px] leading-4 text-[#546A83]">
              {skill.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              enabled={skill.enabled}
              locked={isTogglePending}
              onClick={onToggle}
              testId="custom-skill-detail-enabled-switch"
            />
          </div>
          <InlineIconButton label={`Delete ${skill.frontmatter.name}`} onClick={onDelete}>
            <span aria-hidden="true">
              <IconRemove color="currentColor" size={16} />
            </span>
          </InlineIconButton>
          <InlineIconButton label={`Edit ${skill.frontmatter.name}`} onClick={onEdit}>
            <Pencil aria-hidden="true" className="h-4 w-4" />
          </InlineIconButton>
          {showSharingControls ? (
            <InlineIconButton label={`Share ${skill.frontmatter.name}`} onClick={() => setIsAddingPeople(true)}>
              <Share2 aria-hidden="true" className="h-4 w-4" />
            </InlineIconButton>
          ) : null}
        </div>
      </div>

      {hasDraft ? (
        <div
          className={[
            'flex flex-wrap items-center justify-between gap-3 rounded-[4px] border border-[#0957C3] bg-[#F4F8FE]',
            "px-4 py-3 font-['Open_Sans',sans-serif] text-[14px] leading-5 text-[#0957C3]",
          ].join(' ')}
        >
          <span className="min-w-0 flex-1">Saved changes have not been published to people using this skill.</span>
          <button
            type="button"
            disabled={isPublishing}
            onClick={onPublishDraft}
            className="shrink-0 font-semibold text-[#0957C3] disabled:opacity-50"
          >
            {isPublishing ? 'Publishing...' : 'Push updated skill'}
          </button>
        </div>
      ) : null}

      <section className="rounded-[4px] border border-[#DEE2E7] bg-white">
        <div className="border-b border-[#DEE2E7] px-5 py-4">
          <h2 className="font-['Open_Sans',sans-serif] text-[17px] leading-6 font-normal text-[#262D46]">
            Skill details
          </h2>
        </div>
        <div className="flex flex-col gap-5 px-5 py-5">
          <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-2">
            <CustomSkillDetailField label="Owner">You</CustomSkillDetailField>
            <CustomSkillDetailField label="Last edited">{getUpdatedLabel(skill.updatedAt)}</CustomSkillDetailField>
            <CustomSkillDetailField label="Slash command name">
              <span className="font-mono text-[13px]">/{skill.command}</span>
            </CustomSkillDetailField>
            <CustomSkillDetailField label="Description">{description}</CustomSkillDetailField>
          </dl>
          <CustomSkillPromptPreview skill={skill} />
        </div>
      </section>

      {showSharingControls ? (
        <CustomSkillSharesSummary
          skill={skill}
          isAddingPeople={isAddingPeople}
          onAddPeople={() => setIsAddingPeople(true)}
          onCancelAddPeople={() => setIsAddingPeople(false)}
          onChanged={onSharingChanged}
        />
      ) : null}
    </div>
  );
}
